import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerBanner from "../components/ExpenseTrackerBanner";
import { STATE_TAX_SUMMARY_2024 } from "../data/stateTaxSummary";

// Get current year dynamically
const currentYear = new Date().getFullYear();

/** Small helper: nicely format money */
const fmt = (n: number) =>
  n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });

/** Show a short, friendly summary of the state tax system */
function StateTaxExplainer({
  stateCode,
  filingStatus,
}: {
  stateCode: string;
  filingStatus: string;
}) {
  const s = STATE_TAX_SUMMARY_2024[stateCode as keyof typeof STATE_TAX_SUMMARY_2024];
  if (!s) return null;

  return (
    <div className="mt-3 rounded-lg border bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-base font-semibold">{currentYear} {s.name} income tax</h3>
        <span className="text-xs rounded bg-slate-100 px-2 py-1 uppercase tracking-wide">
          {s.type === "none" ? "No state tax" : s.type === "flat" ? "Flat tax" : "Progressive"}
        </span>
      </div>

      {s.type === "none" && (
        <p className="text-sm text-slate-700">
          {s.name} does not tax wage income. {s.footnote ? <em>{s.footnote}</em> : null}
        </p>
      )}

      {s.type === "flat" && (
        <p className="text-sm text-slate-700">
          Flat rate: <strong>{(s.flatRate! * 100).toFixed(2)}%</strong>
          {s.footnote ? (
            <>
              {" "}
              — <em>{s.footnote}</em>
            </>
          ) : null}
        </p>
      )}

      {s.type === "progressive" && (
        <>
          {s.brackets ? (
            <>
              <p className="mb-2 text-sm text-slate-700">
                Brackets shown below. Filing status: <em>{filingStatus || "—"}</em>.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left">Taxable income</th>
                      <th className="px-3 py-2 text-left">Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.brackets.map((b, i) => {
                      const rowBg = i % 2 === 0 ? "bg-white" : "bg-slate-50/50";
                      return (
                        <tr key={i} className={rowBg}>
                          <td className="px-3 py-2">
                            {b.upTo === null ? "Over prior bracket" : `Up to ${fmt(b.upTo)}`}
                          </td>
                          <td className="px-3 py-2">{(b.rate * 100).toFixed(2)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {s.footnote && <p className="mt-2 text-xs italic text-slate-600">{s.footnote}</p>}
            </>
          ) : (
            <p className="text-sm text-slate-700">
              Progressive bracket system.{" "}
              <em>{s.footnote || `See your state's ${currentYear} DOR table for details.`}</em>
            </p>
          )}
        </>
      )}
    </div>
  );
}

/** Input component – empty string allowed, placeholder “0”, no prefilled 0s */
function MoneyInput({
  label,
  value,
  onChange,
  placeholder = "0",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block group">
      <span className="text-sm font-semibold text-gray-700 mb-1.5 block">{label}</span>
      <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
        <input
          type="text"
          inputMode="decimal"
          className="w-full pl-8 pr-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none text-lg font-medium group-hover:border-gray-300"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
        />
      </div>
    </label>
  );
}

/** Minimal (not final-IRS) math so the screen renders; replace later with official tables */
function quickCalc({
  income1099,
  w2Income,
  businessExpenses,
  retirement,
  homeSqft,
  totalSqft,
  health,
  stateCode,
}: {
  income1099: number;
  w2Income: number;
  businessExpenses: number;
  retirement: number;
  homeSqft: number;
  totalSqft: number;
  health: number;
  stateCode: string;
}) {
  // Simplified home-office (safe default): $5/sqft up to 300
  const homeOffice = Math.min(homeSqft, 300) * 5;

  const netSE = Math.max(0, income1099 - businessExpenses - retirement - homeOffice - health);
  // Approximate SE tax (15.3%) on 92.35% of SE earnings
  const seBase = netSE * 0.9235;
  const seTax = Math.max(0, seBase * 0.153);

  // Very rough federal income tax placeholder (10% after a flat 13k std deduction).
  // You’ll replace with official 2024 IRS brackets shortly.
  const federalTaxable = Math.max(0, netSE + w2Income - 13000 - seTax / 2);
  const federalTax = federalTaxable * 0.10;

  // State quick-look (flat or first bracket if progressive)
  const s = STATE_TAX_SUMMARY_2024[stateCode as keyof typeof STATE_TAX_SUMMARY_2024];
  let stateTax = 0;
  if (s?.type === "flat" && s.flatRate) {
    stateTax = Math.max(0, federalTaxable) * s.flatRate;
  } else if (s?.type === "progressive" && s.brackets?.length) {
    // super simple: apply the first bracket’s rate to all (placeholder)
    stateTax = Math.max(0, federalTaxable) * s.brackets[0].rate;
  }

  const total = federalTax + seTax + stateTax;
  return { homeOffice, seTax, federalTax, stateTax, total };
}

/** ✅ DEFAULT EXPORT — this is the critical bit that fixes your blank screen */
export default function Calculator() {
  // Inputs
  const [state, setState] = useState("OR");
  const [filingStatus, setFilingStatus] = useState("married_joint");
  const [income1099, setIncome1099] = useState(""); // empty by default
  const [w2Income, setW2Income] = useState(""); // new W-2 input
  const [businessExpenses, setBusinessExpenses] = useState("");
  const [retirement, setRetirement] = useState("");
  const [homeSqft, setHomeSqft] = useState("");
  const [totalSqft, setTotalSqft] = useState(""); // reserved for later “regular method”
  const [health, setHealth] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const numbers = {
    income1099: +income1099 || 0,
    w2Income: +w2Income || 0,
    businessExpenses: +businessExpenses || 0,
    retirement: +retirement || 0,
    homeSqft: +homeSqft || 0,
    totalSqft: +totalSqft || 0,
    health: +health || 0,
  };

  const result = useMemo(() => quickCalc({ ...numbers, stateCode: state }), [numbers, state]);

  return (
    <>
      <Helmet>
        <title>Free 1099 Tax Calculator {currentYear} | Federal, State & Self-Employment Tax Estimates</title>
        <meta
          name="description"
          content="Calculate your 1099 taxes instantly. Estimate federal income tax, self-employment tax, state tax, and quarterly payments. Free tool for freelancers, contractors, and self-employed workers."
        />
        <meta name="keywords" content="1099 tax calculator, self employment tax, quarterly taxes, freelance taxes, contractor tax calculator, 1099-NEC, estimated taxes" />
        <link rel="canonical" href="https://free1099calc.com/" />
        
        {/* Open Graph */}
        <meta property="og:title" content={`Free 1099 Tax Calculator ${currentYear}`} />
        <meta property="og:description" content="Calculate federal, state, and self-employment taxes for 1099 contractors and freelancers." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://free1099calc.com/" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Free 1099 Tax Calculator 2024" />
        <meta name="twitter:description" content="Calculate your 1099 taxes instantly - federal, state, and self-employment tax estimates." />
        
        {/* Structured Data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Free 1099 Tax Calculator",
            "description": "Calculate federal, state, and self-employment taxes for 1099 contractors and freelancers",
            "url": "https://free1099calc.com/",
            "applicationCategory": "FinanceApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "creator": {
              "@type": "Organization",
              "name": "Free1099Calc"
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-blue-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1554224311-beee460c201f?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-400/20 via-transparent to-transparent" />
        
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full mb-8 shadow-lg">
              <span className="text-2xl">🧮</span>
              <span className="text-white/90 text-sm font-bold tracking-wide">FREE TAX CALCULATOR</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              Free 1099 Tax
              <span className="block bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">Calculator {currentYear}</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 font-medium mb-10 max-w-3xl mx-auto leading-relaxed">
              Estimate federal, state, and self-employment taxes — plus quarterly payments. Know exactly what to set aside.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-blue-100">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">100% Free</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">All 50 States</span>
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="font-semibold">No Sign Up</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-12">
        {/* Expense Tracker Marketing Banner */}
        <div className="mb-10">
          <ExpenseTrackerBanner />
        </div>
      {/* Inputs (single column) */}
      <section className="mb-10 rounded-2xl border-2 border-gray-200 bg-white p-8 shadow-xl shadow-gray-100">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Calculate Your Tax Estimate</h2>
          <p className="text-gray-600">Enter your income and deductions to get an instant tax calculation.</p>
        </div>
        <div className="grid grid-cols-1 gap-6">
          <MoneyInput
            label="Total 1099 Income"
            value={income1099}
            onChange={setIncome1099}
            placeholder="0"
          />

          <MoneyInput
            label="W-2 Income (optional)"
            value={w2Income}
            onChange={setW2Income}
            placeholder="0"
          />

          <label className="block group">
            <span className="text-sm font-semibold text-gray-700 mb-1.5 block">Filing Status</span>
            <select
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none text-lg font-medium group-hover:border-gray-300 bg-white"
              value={filingStatus}
              onChange={(e) => setFilingStatus(e.target.value)}
            >
              <option value="single">Single</option>
              <option value="married_joint">Married Filing Jointly</option>
              <option value="married_separate">Married Filing Separately</option>
              <option value="head">Head of Household</option>
            </select>
          </label>

          <label className="block group">
            <span className="text-sm font-semibold text-gray-700 mb-1.5 block">State</span>
            <select
              className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all outline-none text-lg font-medium group-hover:border-gray-300 bg-white"
              value={state}
              onChange={(e) => setState(e.target.value)}
            >
              {Object.keys(STATE_TAX_SUMMARY_2024).map((code) => (
                <option key={code} value={code}>
                  {STATE_TAX_SUMMARY_2024[code as keyof typeof STATE_TAX_SUMMARY_2024].name}
                </option>
              ))}
            </select>
          </label>

          {/* State explainer just under the dropdown */}
          <StateTaxExplainer stateCode={state} filingStatus={filingStatus} />

          {/* Advanced toggle */}
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 hover:border-primary-500 hover:bg-primary-50 text-sm font-semibold transition-all"
            onClick={() => setShowAdvanced((v) => !v)}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showAdvanced ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
            </svg>
            {showAdvanced ? "Hide" : "Show"} Advanced Deductions
          </button>

          {/* Advanced panel */}
          {showAdvanced && (
            <div className="mt-6 space-y-6 rounded-2xl border-2 border-primary-200 bg-gradient-to-br from-primary-50 to-blue-50 p-6">
              <div className="flex items-center gap-2 pb-4 border-b border-primary-200">
                <svg className="w-5 h-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <h3 className="font-bold text-primary-900">Advanced Deductions</h3>
              </div>
              <MoneyInput
                label="Business Expenses (supplies, travel, etc.)"
                value={businessExpenses}
                onChange={setBusinessExpenses}
                placeholder="0"
              />
              <MoneyInput
                label="Retirement Contributions (IRA/SEP/Solo 401k)"
                value={retirement}
                onChange={setRetirement}
                placeholder="0"
              />
              <MoneyInput
                label="Health Insurance Premiums"
                value={health}
                onChange={setHealth}
                placeholder="0"
              />
              <MoneyInput
                label="Home Office (sq ft used)"
                value={homeSqft}
                onChange={setHomeSqft}
                placeholder="0"
              />
              <MoneyInput
                label="Total Home Sq Ft (optional)"
                value={totalSqft}
                onChange={setTotalSqft}
                placeholder="0"
              />
            </div>
          )}
        </div>
      </section>

      {/* Two-column results */}
      <section className="grid gap-8 md:grid-cols-2">
        {/* Tax Summary */}
        <div className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 p-8 shadow-xl shadow-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-primary-100 rounded-xl">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Tax Summary</h2>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-primary-200 transition-colors">
              <span className="font-medium text-gray-700">Federal Tax</span>
              <span className="text-xl font-bold text-gray-900">{fmt(result.federalTax)}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-primary-200 transition-colors">
              <span className="font-medium text-gray-700">State Tax ({STATE_TAX_SUMMARY_2024[state].name})</span>
              <span className="text-xl font-bold text-gray-900">{fmt(result.stateTax)}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-primary-200 transition-colors">
              <span className="font-medium text-gray-700">Self-Employment Tax</span>
              <span className="text-xl font-bold text-gray-900">{fmt(result.seTax)}</span>
            </div>
            <div className="flex justify-between items-center p-5 bg-gradient-to-r from-primary-600 to-blue-600 rounded-xl shadow-lg">
              <span className="text-lg font-bold text-white">Total Estimated Tax</span>
              <span className="text-2xl font-black text-white">{fmt(result.total)}</span>
            </div>
          </div>
        </div>

        {/* Quarterly schedule */}
        <div className="rounded-2xl border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 p-8 shadow-xl shadow-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-green-100 rounded-xl">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Quarterly Payments (2024)</h2>
          </div>
          <div className="space-y-3">
            {["Apr 15, 2024", "Jun 17, 2024", "Sep 16, 2024", "Jan 15, 2025"].map((d, i) => (
              <div
                key={d}
                className="flex items-center justify-between p-4 bg-white rounded-xl border-2 border-gray-100 hover:border-green-200 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <span className="text-green-700 font-bold">Q{i + 1}</span>
                  </div>
                  <span className="font-medium text-gray-700">{d}</span>
                </div>
                <span className="text-xl font-bold text-gray-900">{fmt(result.total / 4)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Educational Content Section */}
      <section className="mt-12 rounded-2xl border-2 border-gray-200 bg-white p-8 md:p-10 shadow-xl shadow-gray-100">
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-blue-100 rounded-xl">
            <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
          <h2 className="text-3xl font-black text-gray-900">Understanding Your 1099 Taxes</h2>
        </div>
        
        <div className="space-y-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border-2 border-blue-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              What This Calculator Estimates
            </h3>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">Federal Income Tax:</strong>
                  <span className="text-gray-700"> Based on 2024 tax brackets and your taxable income after deductions</span>
                </div>
              </li>
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">Self-Employment Tax:</strong>
                  <span className="text-gray-700"> 15.3% covering Social Security (12.4%) and Medicare (2.9%) on your net self-employment income</span>
                </div>
              </li>
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">State Income Tax:</strong>
                  <span className="text-gray-700"> Calculated for all 50 states, including states with no income tax</span>
                </div>
              </li>
              <li className="flex gap-3">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <strong className="text-gray-900">Quarterly Payment Schedule:</strong>
                  <span className="text-gray-700"> Your total tax divided by 4 for estimated payments</span>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              How Self-Employment Tax Works
            </h3>
            <p className="text-gray-700 mb-4">
              When you're self-employed, you pay both the employee and employer portions of Social Security and 
              Medicare taxes—a total of <strong className="text-green-700">15.3%</strong> on your net self-employment income.
            </p>
            
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-orange-400 rounded-r-xl p-5 shadow-md">
              <div className="flex gap-3">
                <svg className="w-6 h-6 text-orange-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-bold text-orange-900 mb-1">💡 Tax Tip</p>
                  <p className="text-sm text-gray-700">
                    Most independent contractors should set aside <strong className="text-orange-700">25-30% of every payment</strong> for taxes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
              </svg>
              Common Deductions
            </h3>
            <ul className="space-y-3">
              <li className="flex gap-3">
                <span className="text-purple-600">•</span>
                <div>
                  <strong className="text-gray-900">Business Expenses:</strong>
                  <span className="text-gray-700"> Supplies, software, internet, phone</span>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-purple-600">•</span>
                <div>
                  <strong className="text-gray-900">Home Office:</strong>
                  <span className="text-gray-700"> $5 per sq ft (up to 300 sq ft)</span>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-purple-600">•</span>
                <div>
                  <strong className="text-gray-900">Retirement:</strong>
                  <span className="text-gray-700"> SEP IRA or Solo 401(k) contributions</span>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-purple-600">•</span>
                <div>
                  <strong className="text-gray-900">Health Insurance:</strong>
                  <span className="text-gray-700"> 100% of premiums deductible</span>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="text-purple-600">•</span>
                <div>
                  <strong className="text-gray-900">Mileage:</strong>
                  <span className="text-gray-700"> 67 cents per mile (2024 rate)</span>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-6 border-2 border-red-200">
            <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
              </svg>
              Quarterly Deadlines
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-white rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">Q1</span>
                  <span className="font-semibold text-gray-900">April 15</span>
                </div>
                <span className="text-gray-600">Jan-Mar</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">Q2</span>
                  <span className="font-semibold text-gray-900">June 15</span>
                </div>
                <span className="text-gray-600">Apr-May</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">Q3</span>
                  <span className="font-semibold text-gray-900">September 15</span>
                </div>
                <span className="text-gray-600">Jun-Aug</span>
              </div>
              <div className="flex items-center justify-between p-3 bg-white rounded-xl">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">Q4</span>
                  <span className="font-semibold text-gray-900">January 15</span>
                </div>
                <span className="text-gray-600">Sep-Dec</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
              <svg className="w-7 h-7 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                <path d="M11 17a1 1 0 001.447.894l4-2A1 1 0 0017 15V9.236a1 1 0 00-1.447-.894l-4 2a1 1 0 00-.553.894V17zM15.211 6.276a1 1 0 000-1.788l-4.764-2.382a1 1 0 00-.894 0L4.789 4.488a1 1 0 000 1.788l4.764 2.382a1 1 0 00.894 0l4.764-2.382zM4.447 8.342A1 1 0 003 9.236V15a1 1 0 00.553.894l4 2A1 1 0 009 17v-5.764a1 1 0 00-.553-.894l-4-2z" />
              </svg>
              Related Tools & Resources
            </h3>
            <p className="text-gray-600 mb-6 text-center">
              Maximize your financial planning with our integrated suite of freelancer tools:
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Link to="/retirement" className="group block bg-white border-2 border-blue-200 rounded-2xl p-6 hover:border-blue-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-600 transition-colors">
                    <svg className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                      <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-lg text-gray-900 group-hover:text-blue-600 transition-colors">Retirement Calculator</h4>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Project your nest egg with inflation-adjusted returns. See how much to save monthly in today's dollars.
                </p>
              </Link>
              
              <Link to="/calculators/inflation" className="group block bg-white border-2 border-purple-200 rounded-2xl p-6 hover:border-purple-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-600 transition-colors">
                    <svg className="w-6 h-6 text-purple-600 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-lg text-gray-900 group-hover:text-purple-600 transition-colors">Inflation Calculator</h4>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Understand how inflation affects your income. Learn why raising rates annually is essential.
                </p>
              </Link>
              
              <Link to="/trends" className="group block bg-white border-2 border-green-200 rounded-2xl p-6 hover:border-green-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-600 transition-colors">
                    <svg className="w-6 h-6 text-green-600 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-lg text-gray-900 group-hover:text-green-600 transition-colors">Job Market Trends</h4>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Industry-specific rate benchmarks and career planning strategies for freelancers.
                </p>
              </Link>
              
              <Link to="/faq" className="group block bg-white border-2 border-orange-200 rounded-2xl p-6 hover:border-orange-400 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 bg-orange-100 rounded-lg group-hover:bg-orange-600 transition-colors">
                    <svg className="w-6 h-6 text-orange-600 group-hover:text-white transition-colors" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h4 className="font-bold text-lg text-gray-900 group-hover:text-orange-600 transition-colors">Tax FAQ</h4>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Quick answers about quarterly deadlines, deductions, and common 1099 tax questions.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Learn more (SEO + trust) */}
      <section className="mt-10 rounded-3xl border-2 border-gray-200 bg-gradient-to-br from-white via-gray-50 to-blue-50 p-8 md:p-10 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg">
            <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <h2 className="text-3xl font-black text-gray-900">About This Free 1099 Calculator</h2>
        </div>
        <p className="text-lg text-gray-700 leading-relaxed">
          This tool helps self-employed professionals estimate taxes using {currentYear} rules. It's a
          planning aid only — always confirm with a CPA before filing.
        </p>

        <div className="mt-8 bg-white rounded-2xl p-6 border-2 border-blue-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
            </svg>
            Who Should Use It?
          </h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Contractors and freelancers with 1099-NEC or 1099-K income</span>
            </li>
            <li className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Gig workers (rideshare, delivery, marketplaces)</span>
            </li>
            <li className="flex items-start gap-3">
              <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
              <span className="text-gray-700">Sole proprietors and small business owners</span>
            </li>
          </ul>
        </div>

        <div className="mt-6 bg-white rounded-2xl p-6 border-2 border-purple-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11 4a1 1 0 10-2 0v4a1 1 0 102 0V7zm-3 1a1 1 0 10-2 0v3a1 1 0 102 0V8zM8 9a1 1 0 00-2 0v2a1 1 0 102 0V9z" />
            </svg>
            How the Math Works (simplified)
          </h3>
          <p className="text-gray-700 leading-relaxed">
            We start with gross 1099 income, subtract business expenses and simplified home-office
            deduction (up to 300 sq ft × $5). We estimate self-employment tax on 92.35% of net
            earnings and deduct half of SE tax from AGI. We then calculate a placeholder federal tax
            and combine with a state quick-look rate. We will swap in official IRS/state brackets in a
            later update for full precision.
          </p>
        </div>

        <div className="mt-6 bg-white rounded-2xl p-6 border-2 border-green-100">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
            </svg>
            Planning Tips
          </h3>
          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-gray-700">Update your inputs each quarter to keep savings on track.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-gray-700">Keep receipts and mileage logs so you don't leave deductions on the table.</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="text-green-600 font-bold flex-shrink-0">✓</span>
              <span className="text-gray-700">If income varies, set aside a flat percentage from each payment.</span>
            </li>
          </ul>
        </div>

        <div className="mt-6 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-2xl p-6 border-l-4 border-yellow-400">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Disclaimer
          </h3>
          <p className="text-gray-700 leading-relaxed">
            This calculator is for educational purposes only and isn't tax or legal advice. Tax laws
            change and your situation may differ. Always confirm results with a licensed CPA or tax
            professional before filing or making payments.
          </p>
        </div>
      </section>

      {/* Expense Tracker CTA */}
      <section className="mt-10 rounded-3xl bg-gradient-to-br from-primary-600 via-primary-700 to-blue-900 text-white p-10 md:p-12 shadow-2xl border-4 border-white/20 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-400/10 rounded-full blur-2xl -ml-24 -mb-24"></div>
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full px-5 py-2 mb-6 shadow-lg">
            <span className="text-2xl mr-2">💰</span>
            <span className="text-sm font-bold text-white">NEW PREMIUM TOOL</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black mb-4 leading-tight">
            Track Expenses & Know Your Audit Risk
          </h2>
          
          <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
            Don't just calculate taxes—know which deductions are safe! Our new Expense Tracker gives you a real-time audit risk score and tells you exactly what documentation you need.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/tools/expense-tracker"
              className="group inline-flex items-center justify-center px-10 py-5 bg-white text-primary-900 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-blue-500/50 hover:scale-105"
            >
              Try Free (5 Expenses)
              <svg className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-8 text-base text-white/90">
            <div className="flex items-center gap-2">
              <div className="p-1 bg-green-400 rounded-full">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="font-semibold">14-Day Free Trial</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1 bg-green-400 rounded-full">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="font-semibold">Then $5.99/month</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-1 bg-green-400 rounded-full">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="font-semibold">Cancel Anytime</span>
            </div>
          </div>
        </div>
      </section>

      <RecentPosts />
    </main>
    </>
  );
}
